import type { PaginationState } from '@tanstack/react-table'
import { Box } from 'designSystem/components/Box'
import { DocumentCardResultCountLabel } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/DocumentCardResultCountLabel'
import { PaginationControls } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/PaginationControls'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useDocumentCardPagination } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentCardPagination'
import { SyncedTableHorizontalScrollbar } from 'features/knowledgeDocumentViewer/components/table/SyncedTableHorizontalScrollbar'
import type { ViewMode } from 'features/knowledgeDocumentViewer/types'
import type { ReactElement, RefObject } from 'react'
import { useCallback } from 'react'
import styled from 'styled-components'

const TableScrollbarContainer = styled.div<{ $widthPercent: number }>`
  width: ${({ $widthPercent }) => `${$widthPercent}%`};
  max-width: 100%;
  min-width: 0;
`

import { DocumentViewModeToggle } from './DocumentViewModeToggle'

interface DocumentResultsFooterProps {
  activeIndex: number
  documentTotalCount: number
  filteredDocuments: DocumentTableRow[]
  hasActiveFilters: boolean
  pagination: PaginationState
  setActiveIndex: (index: number) => void
  setPagination: (
    updater: PaginationState | ((prev: PaginationState) => PaginationState)
  ) => void
  tableScrollbarWidthPercent?: number
  tableFilteredCount: number
  tableHasActiveFilters: boolean
  tableScrollContainerRef?: RefObject<HTMLElement | null>
  viewMode: ViewMode
  onViewModeChange: (value: string | number) => void
}

export function DocumentResultsFooter({
  activeIndex,
  documentTotalCount,
  filteredDocuments,
  hasActiveFilters,
  pagination,
  setActiveIndex,
  setPagination,
  tableScrollbarWidthPercent = 100,
  tableFilteredCount,
  tableHasActiveFilters,
  tableScrollContainerRef,
  viewMode,
  onViewModeChange,
}: DocumentResultsFooterProps): ReactElement {
  const cardPagination = useDocumentCardPagination({
    filteredDocuments,
    totalCount: documentTotalCount,
    filteredCount: filteredDocuments.length,
    hasActiveFilters,
    activeIndex,
    onActiveIndexChange: setActiveIndex,
  })

  const tableDisplayCount = tableHasActiveFilters
    ? tableFilteredCount
    : documentTotalCount
  const tablePageStart = pagination.pageIndex * pagination.pageSize
  const tablePageEnd = Math.min(
    tablePageStart + pagination.pageSize,
    tableFilteredCount
  )
  const tableTotalPages = Math.ceil(tableFilteredCount / pagination.pageSize)

  const handleTablePageChange = useCallback(
    (page: number) => {
      setPagination((prev) => ({ ...prev, pageIndex: page }))
    },
    [setPagination]
  )
  const getTableViewport = useCallback(
    () =>
      tableScrollContainerRef?.current?.querySelector<HTMLElement>(
        '.rt-ScrollAreaViewport'
      ),
    [tableScrollContainerRef]
  )

  const paginationProps =
    viewMode === 'card'
      ? {
          currentPage: cardPagination.currentPage,
          totalPages: cardPagination.totalPages,
          onPageChange: cardPagination.handlePageChange,
          displayCount: cardPagination.displayCount,
          pageEnd: cardPagination.pageEnd,
          pageStart: cardPagination.pageStart,
          resultCount: filteredDocuments.length,
          hasActiveFilters,
        }
      : {
          currentPage: pagination.pageIndex,
          totalPages: tableTotalPages,
          onPageChange: handleTablePageChange,
          displayCount: tableDisplayCount,
          pageEnd: tablePageEnd,
          pageStart: tablePageStart,
          resultCount: tableFilteredCount,
          hasActiveFilters: tableHasActiveFilters,
        }

  return (
    <Box shrink={false} width='full'>
      {viewMode === 'table' && tableScrollContainerRef && (
        <TableScrollbarContainer $widthPercent={tableScrollbarWidthPercent}>
          <SyncedTableHorizontalScrollbar
            enabled
            getViewport={getTableViewport}
          />
        </TableScrollbarContainer>
      )}
      <PaginationControls
        currentPage={paginationProps.currentPage}
        resultCountLabel={
          <DocumentCardResultCountLabel
            displayCount={paginationProps.displayCount}
            hasActiveFilters={paginationProps.hasActiveFilters}
            pageEnd={paginationProps.pageEnd}
            pageStart={paginationProps.pageStart}
            resultCount={paginationProps.resultCount}
            totalCount={documentTotalCount}
          />
        }
        totalPages={paginationProps.totalPages}
        rightContent={
          <DocumentViewModeToggle
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
          />
        }
        onPageChange={paginationProps.onPageChange}
      />
    </Box>
  )
}
